import moment from 'moment-timezone';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { DateFormat, EventActionViewModel, UserViewModel } from 'stageclip-common';

import { createAllEventActionsRequestedAction } from '../../actions/eventActionsActions';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { ById } from '../../store/normalizedState';
import { getAllWithIds, getItem } from '../../store/selectors';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';
import { AppTable, AppTableColumn } from '../table/appTable';
import { FullName } from '../users/fullName';
import { SimpleModalId } from '../../constants/modalId';
import { createEventArchivedActionsRequestedAction } from '../../actions/eventArchivedActionsActions';
import { ActionLogDetailsModal } from '../actionLogDetailsModal/ActionLogDetailsModal';

import { createModalOpenAction } from 'actions/modalActions';
import Button from 'components/buttons/button';

interface ActionsLogTableOwnProps {
  eventId: string;
  archived: boolean;
}

interface ActionsLogTableStateProps extends BaseStateProps {
  eventActions: EventActionViewModel[];
  users: ById<UserViewModel>;
}

interface ActionsLogTableDispatchProps extends BaseDispatchProps {
  loadAllEventActions: (c: ActionsLogTable) => void;
  openDetails: (data: { [T in keyof EventActionViewModel]?: EventActionViewModel[T] }) => void;
}

class ActionsLogTable extends ComponentBase<
  ActionsLogTableOwnProps,
  ActionsLogTableStateProps,
  ActionsLogTableDispatchProps
> {
  private columns: AppTableColumn[] = [
    {
      Header: 'Action',
      accessor: (a: EventActionViewModel) => a.action,
      id: 'action',
    },
    {
      // eslint-disable-next-line react/destructuring-assignment
      Cell: (row: any) => (row.value ? <FullName user={this.props.users[row.value]} /> : '-'),
      Header: 'User',
      accessor: (a: EventActionViewModel) => a.user,
      id: 'user',
    },
    {
      Header: 'Timestamp',
      accessor: (a: EventActionViewModel) => moment(a.timestamp).format(DateFormat.DateTime),
      id: 'timestamp',
    },
    {
      Cell: ({ value }: { value: EventActionViewModel }) => {
        const { openDetails } = this.props;
        const { data, action, timestamp } = value;

        return data ? <Button onClick={() => openDetails({ data, action, timestamp })} text="Details" /> : '-';
      },
      Header: 'Details',
      accessor: (event: EventActionViewModel) => event,
      id: 'details',
    },
  ];

  protected dataLoaded(props: any) {
    return props.eventActions !== undefined;
  }

  protected async loadData(props: any) {
    await props.loadAllEventActions(this);
  }

  protected loggedInRender() {
    const actionsOrderedByTimestamp = this.props.eventActions.sort((a, b) =>
      moment(a.timestamp).isBefore(b.timestamp) ? -1 : 1,
    );

    return (
      <>
        <AppTable columns={this.columns} tableName="eventActions" data={actionsOrderedByTimestamp} />
        <ActionLogDetailsModal />
      </>
    );
  }
}

function mapStateToProps(state: ClippingPlatformState, ownProps: ActionsLogTableOwnProps): ActionsLogTableStateProps {
  const event =
    getItem(state.entities.events, ownProps.eventId) || getItem(state.entities.archivedEvents, ownProps.eventId);

  return {
    ...baseMapStateToProps(state),
    eventActions: event.eventActions ? getAllWithIds(state.entities.eventActions, event.eventActions) : undefined,
    users: state.entities.users.byId,
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: ActionsLogTableOwnProps): ActionsLogTableDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    loadAllEventActions: (c: ActionsLogTable) =>
      dispatch(
        !ownProps.archived
          ? createAllEventActionsRequestedAction(ownProps.eventId, c, dispatch)
          : createEventArchivedActionsRequestedAction(ownProps.eventId, c, dispatch),
      ),
    openDetails: data => dispatch(createModalOpenAction(SimpleModalId.LogDetails, data)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionsLogTable);
